/**
 * The canvas work of the Desktop mascot gallery card (DesktopMascotCard.tsx) and its Add several outfits section
 * (BulkOutfitsSection.tsx): decoding an image to its exact pixels, "Prepare for desktops", and checking one file for the
 * list with a small picture of it. The pure steps and the rules are in lib/desktopMascot.ts and lib/bulkOutfits.ts, with
 * their tests; the service checks every upload again.
 */
import {
  BULK_THUMB_PX,
  DECODE_FAILED,
  PREPARE_FAILED,
  bulkPreparation,
  isAnimatedWebp,
  preparationNote,
  sniffImageFormat,
  type BulkFileAnalysis,
} from '../lib/bulkOutfits';
import {
  PREPARE,
  alphaPlaneOf,
  analyzeAlpha,
  checkMascotPng,
  cleanAlpha,
  encodeWithinLimit,
  fitInSquare,
  marginCrop,
  mascotWarnings,
  mirrorRgba,
  opaqueBounds,
  pixelRefusal,
  preparedLine,
  sha256Hex,
  type AlphaFindings,
  type MascotRect,
} from '../lib/desktopMascot';

export async function decodeBitmap(source: Blob): Promise<ImageBitmap> {
  try {
    // The exact pixels: no premultiplication and no colour conversion on the way in, as the agent decodes them.
    return await createImageBitmap(source, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  } catch {
    return createImageBitmap(source);
  }
}

export function canvas2d(width: number, height: number, readBack = false): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: readBack });
  if (!context) throw new Error('This browser cannot draw the image.');
  return { canvas, context };
}

export function pixelsOf(bitmap: ImageBitmap): ImageData {
  const { context } = canvas2d(bitmap.width, bitmap.height, true);
  context.drawImage(bitmap, 0, 0);
  return context.getImageData(0, 0, bitmap.width, bitmap.height);
}

export function canvasPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The browser could not encode the PNG.'))), 'image/png');
  });
}

/**
 * "Prepare for desktops", on canvases: (1) mirror when asked, (2) haze to 0 and a nearly solid body to 255, with the RGB
 * of clear pixels zeroed, (3) trim to the visible art plus a 6 px transparent margin, (4) fit within 1024 px, (5) encode
 * as PNG, (6) while over 768,000 bytes, shrink about 6% and encode again, down to 512 px. The pure steps and their tests
 * are in lib/desktopMascot.ts; the service checks the result like any upload.
 */
export async function prepareForDesktops(file: File, mirror: boolean): Promise<{ ok: true; blob: Blob; line: string } | { ok: false; message: string }> {
  const bitmap = await decodeBitmap(file);
  try {
    const { width, height } = bitmap;
    const pixels = pixelsOf(bitmap);
    if (mirror) mirrorRgba(pixels.data, width, height);
    cleanAlpha(pixels.data);
    const bounds = opaqueBounds(alphaPlaneOf(pixels.data), width, height);
    if (!bounds) return { ok: false, message: 'The image is completely transparent, so desktops would show nothing. Check the export.' };
    // The cleaned art on a copy with a transparent border, so the crop with its margin never leaves the canvas.
    const margin = PREPARE.marginPx;
    const padded = canvas2d(width + 2 * margin, height + 2 * margin);
    padded.context.putImageData(pixels, margin, margin);
    const crop = marginCrop(bounds, margin);
    const out = canvas2d(1, 1);
    const encoded = await encodeWithinLimit(crop, async (size) => {
      out.canvas.width = size.width;
      out.canvas.height = size.height;
      // Resizing a canvas resets its drawing state, so the smoothing is set again for every attempt.
      out.context.imageSmoothingEnabled = true;
      out.context.imageSmoothingQuality = 'high';
      out.context.clearRect(0, 0, size.width, size.height);
      out.context.drawImage(padded.canvas, crop.x, crop.y, crop.width, crop.height, 0, 0, size.width, size.height);
      const blob = await canvasPng(out.canvas);
      return { result: blob, bytes: blob.size };
    });
    if (!encoded.ok) return encoded;
    return {
      ok: true,
      blob: encoded.result,
      line: preparedLine({
        mirrored: mirror,
        trimmed: crop.width < width || crop.height < height,
        resized: encoded.size.width !== crop.width || encoded.size.height !== crop.height,
        from: { width, height, bytes: file.size },
        to: { ...encoded.size, bytes: encoded.bytes },
      }),
    };
  } finally {
    bitmap.close();
  }
}

/**
 * A small picture of an image for the list, as an object URL of a PNG: the box of pixels that catch clicks (the whole
 * image when none is known), its longer side at most BULK_THUMB_PX. The caller revokes the URL.
 */
export async function thumbnailUrl(bitmap: ImageBitmap, bounds: MascotRect | null): Promise<string | null> {
  const box = bounds ?? { x: 0, y: 0, width: bitmap.width, height: bitmap.height };
  if (box.width <= 0 || box.height <= 0) return null;
  const size = fitInSquare(Math.min(BULK_THUMB_PX, Math.max(box.width, box.height)), box.width, box.height);
  const { canvas, context } = canvas2d(size.width, size.height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, box.x, box.y, box.width, box.height, 0, 0, size.width, size.height);
  return URL.createObjectURL(await canvasPng(canvas));
}

/**
 * Add several outfits: check one chosen file as Add an outfit checks it (its bytes, never its name), prepare it for
 * desktops when it needs it (lib/bulkOutfits.ts bulkPreparation: mirrored when asked, too large, too big a side, not a
 * 32-bit PNG, or a WebP), check what would go up again with the service's rules and the pixel rules, and draw its picture:
 * the outfit trimmed, and mirrored when asked, as desktops will show it. A file that cannot be added still gets a picture
 * when the browser can draw it, so the admin can tell which one it is. The list checks one file at a time, so one image
 * at most is decoded at once; a row keeps only the prepared PNG (at most 768,000 bytes) or the chosen file itself, and
 * its picture.
 */
export async function analyzeOutfitFile(file: File, mirror: boolean): Promise<BulkFileAnalysis> {
  const original = new Uint8Array(await file.arrayBuffer());
  const format = sniffImageFormat(original);
  const check = format === 'png' ? checkMascotPng(original) : null;
  const plan = bulkPreparation(format, check, mirror, format === 'webp' && isAnimatedWebp(original));
  let upload: Blob = file;
  let bytes = original;
  let finalCheck = check;
  let prepared: string | null = null;
  let refusal: string | null = plan.kind === 'refused' ? plan.message : null;
  if (plan.kind === 'prepare') {
    try {
      const result = await prepareForDesktops(file, mirror);
      if (result.ok) {
        upload = result.blob;
        bytes = new Uint8Array(await result.blob.arrayBuffer());
        finalCheck = checkMascotPng(bytes);
        prepared = preparationNote(format, check, result.line);
      } else {
        refusal = result.message;
      }
    } catch {
      refusal = PREPARE_FAILED;
    }
  }
  refusal ??= finalCheck?.refusal?.message ?? null;
  let findings: AlphaFindings | null = null;
  let drawn: { width: number; height: number } | null = null;
  let thumbUrl: string | null = null;
  try {
    const bitmap = await decodeBitmap(refusal === null ? upload : file);
    try {
      drawn = { width: bitmap.width, height: bitmap.height };
      if (refusal === null) {
        findings = analyzeAlpha(alphaPlaneOf(pixelsOf(bitmap).data), bitmap.width, bitmap.height);
        refusal = pixelRefusal(findings)?.message ?? null;
      }
      try {
        thumbUrl = await thumbnailUrl(bitmap, findings?.bounds ?? null);
      } catch {
        // No picture: the row still says what the file is and whether it can be added.
      }
    } finally {
      bitmap.close();
    }
  } catch {
    refusal ??= DECODE_FAILED;
  }
  const accepted = refusal === null;
  let sha256: string | null = null;
  try {
    sha256 = accepted ? await sha256Hex(bytes) : null;
  } catch (error) {
    if (thumbUrl) URL.revokeObjectURL(thumbUrl);
    throw error;
  }
  return {
    upload: accepted ? upload : null,
    width: finalCheck?.header?.width ?? drawn?.width ?? 0,
    height: finalCheck?.header?.height ?? drawn?.height ?? 0,
    sizeBytes: accepted ? bytes.length : original.length,
    sha256,
    prepared: accepted ? prepared : null,
    refusal,
    warnings: accepted ? mascotWarnings(finalCheck?.header ?? null, findings) : [],
    thumbUrl,
  };
}
